import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useHasCseMessagesAccess } from "@/features/cse-messages/hooks/useHasCseMessagesAccess";
import { getCseMessages } from "@/features/cse-messages/services/cseMessages.service";
import type { CseMessage } from "@/features/cse-messages/types/cseMessage.type";

export const CSE_MESSAGES_QUERY_KEY = ["cse-messages"] as const;

export type UseCseMessagesReturn = {
  modalQueue: CseMessage[]
  banners: CseMessage[]
  toasters: CseMessage[]
  // Vrai quand la file est définitive (fetch terminé, en succès ou en échec,
  // ou requête désactivée) : permet à d'autres modales d'attendre son verdict.
  isSettled: boolean
};

// Sémantique « à l'ouverture de l'app » : un seul fetch par session. Le
// dismiss retire les messages du cache de façon optimiste, donc aucun refetch
// ne doit pouvoir les faire réapparaître en cours de session.
export const useCseMessages = (): UseCseMessagesReturn => {
  const { isAuthenticated } = useAuth();
  const hasAccess = useHasCseMessagesAccess();

  const isQueryEnabled = isAuthenticated && hasAccess;

  const { data, isFetched } = useQuery({
    queryKey: CSE_MESSAGES_QUERY_KEY,
    queryFn: getCseMessages,
    enabled: isQueryEnabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // hidden_features peut se résoudre après un premier fetch : l'accès reste
  // autoritaire sur l'affichage, même si le cache contient déjà des messages.
  const messages = hasAccess ? data ?? [] : [];

  return {
    modalQueue: messages.filter((message) => message.format === "modal"),
    banners: messages.filter((message) => message.format === "banner"),
    toasters: messages.filter((message) => message.format === "toaster"),
    isSettled: !isQueryEnabled || isFetched,
  };
};
